import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'

const STATUS_LABELS: Record<string, string> = {
  free: 'Свободен',
  reserved: 'Забронирован',
  in_use: 'В аренде',
  service: 'Обслуживание',
}

const STATUS_COLORS: Record<string, string> = {
  free: 'bg-green-100 text-green-700',
  reserved: 'bg-yellow-100 text-yellow-700',
  in_use: 'bg-blue-100 text-blue-700',
  service: 'bg-orange-100 text-orange-700',
}

function fmt(n: number) {
  return n.toLocaleString('ru-RU') + ' ₽'
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function fmtElapsed(isoStart: string): string {
  const ms = Date.now() - new Date(isoStart).getTime()
  const minutes = Math.floor(ms / 60000)
  if (minutes < 60) return `${minutes} мин`
  const h = Math.floor(minutes / 60); const m = minutes % 60
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`
}

export default function BikePublicPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const qr = params.get('qr') ?? ''

  // If manager is logged in, redirect to scan page with this QR
  useEffect(() => {
    if (user?.role === 'manager' && qr) {
      navigate(`/manager/scan?qr=${encodeURIComponent(qr)}`, { replace: true })
    }
  }, [user, qr, navigate])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-bike', qr],
    queryFn: () => api.get('/bikes/public/bike', { params: { qr } }).then((r) => r.data),
    enabled: !!qr && user?.role !== 'manager',
    retry: false,
  })

  if (!qr) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center text-gray-400">
          <div className="text-4xl mb-2">🚲</div>
          <p>QR-код не найден</p>
        </div>
      </div>
    )
  }

  if (isLoading || user?.role === 'manager') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400 text-sm">Загрузка...</div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center text-gray-400">
          <div className="text-4xl mb-2">❌</div>
          <p className="text-sm">Велосипед не найден или недоступен</p>
        </div>
      </div>
    )
  }

  const { bike, tariff, tenant, activeRental } = data ?? {}

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-8 max-w-md mx-auto">
      {/* Tenant logo + name */}
      <div className="mb-6 text-center">
        <img
          src={`/logos/${encodeURIComponent(tenant?.name ?? '')}.png`}
          alt={tenant?.name}
          className="h-16 mx-auto mb-2 object-contain"
          onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/logo.png' }}
        />
        <p className="text-lg font-bold text-gray-800">{tenant?.name}</p>
      </div>

      {/* Bike card */}
      <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">#{bike?.code}</h1>
            {bike?.model && <p className="text-sm text-gray-500">{bike.model}</p>}
            {bike?.pointName && <p className="text-xs text-gray-400 mt-0.5">📍 {bike.pointName}</p>}
          </div>
          <span className={`text-sm px-3 py-1 rounded-full font-medium ${STATUS_COLORS[bike?.status ?? 'free'] ?? 'bg-gray-100 text-gray-600'}`}>
            {STATUS_LABELS[bike?.status ?? 'free'] ?? bike?.status}
          </span>
        </div>

        {tariff && (
          <div className="flex gap-3 border-t pt-3">
            <div className="flex-1 text-center bg-gray-50 rounded-xl py-2">
              <p className="text-xs text-gray-400 mb-0.5">В час</p>
              <p className="font-bold text-base">{fmt(tariff.hourlyRate)}</p>
            </div>
            {tariff.dailyRate && (
              <div className="flex-1 text-center bg-gray-50 rounded-xl py-2">
                <p className="text-xs text-gray-400 mb-0.5">В сутки</p>
                <p className="font-bold text-base">{fmt(tariff.dailyRate)}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Active rental info */}
      {activeRental && (
        <div className="w-full bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-4">
          <p className="text-sm font-semibold text-blue-700 mb-3">🔄 Велосипед сейчас в аренде</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Тариф</span>
              <span className="font-medium">{activeRental.tariffName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Начало</span>
              <span className="font-medium">{fmtTime(activeRental.startAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Время в аренде</span>
              <span className="font-medium">{fmtElapsed(activeRental.startAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Планируемый возврат</span>
              <span className="font-medium">{fmtTime(activeRental.plannedReturnAt)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-gray-600 font-medium">Текущая стоимость</span>
              <span className="font-bold text-blue-800">{fmt(activeRental.currentEstimate)}</span>
            </div>
            {activeRental.prepayment > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Предоплата</span>
                <span className="font-medium text-green-600">{fmt(activeRental.prepayment)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA for free bike */}
      {bike?.status === 'free' && (
        <div className="w-full bg-green-50 border border-green-200 rounded-2xl p-5 mb-4 text-center">
          <div className="text-3xl mb-2">🚲</div>
          <p className="font-semibold text-green-800 mb-1">Велосипед свободен!</p>
          <p className="text-sm text-green-700">Обратитесь к сотруднику для оформления аренды</p>
        </div>
      )}

      {/* Contact */}
      {tenant?.contactPhone && (
        <a
          href={`tel:${tenant.contactPhone.replace(/\s/g, '')}`}
          className="w-full bg-brand-600 text-white text-center py-4 rounded-2xl font-semibold text-base shadow-sm active:opacity-80"
        >
          📞 {tenant.contactPhone}
        </a>
      )}

      <p className="mt-6 text-xs text-gray-300">VeloRent · Система управления прокатом</p>
    </div>
  )
}
